import moment from 'moment';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { Badge } from 'framework/ui/mui-builder';
import type { ListTableColumn } from 'framework/ui/ui-builder';
import { DATE_FORMAT } from 'framework/utils';
import { FilterBar, PageContentProcessTabs } from 'framework/ViewTypes/components';
import { Artifacts, RelatedList, Timeline } from 'framework/ViewTypes/embeddable';
import type {
  ArtifactItem,
  TimelineConfig,
  TimelineRecordAuthorProps,
  TimelineSourceRecord,
} from 'framework/ViewTypes/embeddable';
import type { ApplicationOffer, ApplicationTimelineEvent } from '../shared/types';
import { FilterCondition } from 'framework/types';

type ApplicationActivityTabsProps = {
  timeline: ApplicationTimelineEvent[];
  artifacts: ArtifactItem[];
  offers: ApplicationOffer[];
};

const toDisplayDate = (value: string, format: string) => moment(value).format(format);

const APPLICATION_TIMELINE_TYPE = 'APPLICATION_ACTIVITY' as const;

type ApplicationTimelineRecord = ApplicationTimelineEvent & TimelineSourceRecord<typeof APPLICATION_TIMELINE_TYPE>;

const ApplicationTimelineRecordAuthor = ({ id, sx }: TimelineRecordAuthorProps) => (
  <Box sx={sx}>
    <Typography variant="body2">{id}</Typography>
  </Box>
);

const APPLICATION_TIMELINE_CONFIG: TimelineConfig<ApplicationTimelineRecord> = {
  RecordAuthor: ApplicationTimelineRecordAuthor,
  types: {
    [APPLICATION_TIMELINE_TYPE]: {
      mapItem: event => ({
        id: event.id,
        type: event.type,
        actor: event.actor,
        occurredAt: event.occurredAt,
        description: event.title,
      }),
    },
  },
};

const toTimelineRecord = (event: ApplicationTimelineEvent): ApplicationTimelineRecord => ({
  ...event,
  type: APPLICATION_TIMELINE_TYPE,
});

const OFFER_COLUMNS: ListTableColumn<ApplicationOffer>[] = [
  { id: 'id', label: 'Offer id', render: offer => <Typography color="primary">{offer.id}</Typography> },
  { id: 'author', label: 'Author', render: offer => offer.author },
  {
    id: 'status',
    label: 'Status',
    render: offer => <Badge>{offer.status}</Badge>,
  },
  {
    id: 'lastInteractionAt',
    label: 'Last interaction',
    align: 'right',
    render: offer => toDisplayDate(offer.lastInteractionAt, DATE_FORMAT.SIMPLE),
  },
];

const ApplicationActivityTabs = ({ timeline, artifacts, offers }: ApplicationActivityTabsProps) => (
  <PageContentProcessTabs
    tabs={[
      {
        id: 'timeline',
        name: 'Timeline',
        panel: <Timeline items={timeline.map(toTimelineRecord)} config={APPLICATION_TIMELINE_CONFIG} />,
      },
      {
        id: 'artifacts',
        name: 'Artifacts',
        panel: (
          <Artifacts
            items={artifacts.map(artifact => ({
              ...artifact,
              uploadedAt: artifact.uploadedAt && toDisplayDate(artifact.uploadedAt, DATE_FORMAT.FULL_DATE),
            }))}
            uploadAffordanceProps={{ description: 'Up to 15 files, max 1 Gb each' }}
          />
        ),
      },
      ...(offers.length
        ? [
            {
              id: 'offers',
              name: 'Offers',
              badge: offers.length,
              panel: <RelatedList listProps={{ columns: OFFER_COLUMNS, rows: offers }} />,
            },
          ]
        : []),
    ]}
    actions={[
      <FormControlLabel
        key="activities"
        control={<Switch defaultChecked size="small" />}
        label={<Typography variant="caption">Show all activities</Typography>}
        sx={{ mr: 0 }}
      />,
      <FilterBar
        key="filter"
        filterProps={{
          value: {},
          onChange: () => undefined,
          filters: [
            {
              id: 'activityType',
              source: 'activityType',
              condition: FilterCondition.IN,
              type: 'dropdown',
              label: 'Activity type',
              multiple: true,
              options: [],
            },
          ],
        }}
      />,
    ]}
  />
);

export default ApplicationActivityTabs;
